<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "./stores/auth.js";
import { useThemeStore } from "./stores/theme.js";

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const theme = useThemeStore();

const showChrome = computed(() => !route.meta.public);

const themeIcon = computed(() => ({ light: "☀️", dark: "🌙", system: "🖥️" })[theme.preference]);

async function handleLogout(): Promise<void> {
  await auth.logout();
  router.push({ name: "login" });
}
</script>

<template>
  <div class="min-h-full flex flex-col">
    <header
      v-if="showChrome"
      class="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
    >
      <div class="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3 sm:gap-6">
        <span class="font-semibold tracking-tight shrink-0">Axiom</span>
        <nav class="flex gap-3 sm:gap-4 text-sm shrink-0">
          <router-link
            :to="{ name: 'projects' }"
            class="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            active-class="text-slate-900 dark:text-white font-medium"
          >
            Projects
          </router-link>
          <router-link
            v-if="auth.isAdmin"
            :to="{ name: 'users' }"
            class="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            active-class="text-slate-900 dark:text-white font-medium"
          >
            Users
          </router-link>
        </nav>
        <div class="ml-auto flex items-center gap-3 sm:gap-4 text-sm min-w-0">
          <button
            type="button"
            class="text-slate-500 hover:text-slate-900 dark:hover:text-white shrink-0"
            :title="`Theme: ${theme.preference} (click to cycle)`"
            @click="theme.cycle()"
          >
            {{ themeIcon }}
          </button>
          <router-link
            :to="{ name: 'account' }"
            class="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white truncate hidden sm:block"
          >
            {{ auth.user?.name }}
          </router-link>
          <button
            type="button"
            class="text-slate-500 hover:text-slate-900 dark:hover:text-white"
            @click="handleLogout"
          >
            Log out
          </button>
        </div>
      </div>
    </header>

    <main class="flex-1">
      <router-view />
    </main>
  </div>
</template>
